import React, { useState, useEffect, useContext } from 'react';
import Layout from '@/components/Layout';
import { ArrowLeft, Clock, CheckCircle, Play, Trash2, Shuffle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionNames = {
  'acupuncture': 'Acupuncture',
  'biomedicine': 'Biomedicine',
  'foundations': 'Foundations',
  'herbs': 'Herbs',
  'law-safety': 'Law & Safety',
  'luo-vessels': 'Luo Vessels',
  'divergent-vessels': 'Divergent Vessels',
  'adjunct-modalities': 'Adjunct Modalities',
  'nutrition': 'Nutrition',
  'alternate-needling': 'Alternate Needling Styles',
  'advanced-needle': 'Advanced Needle Technique',
  'sports-medicine': 'Sports Medicine Acupuncture',
};

const tierNames = {
  'basic': 'Basic Practice',
  'comps-ready': 'Comps Ready',
  'advanced': 'Advanced',
};

export default function TestingSectionPage() {
  const { tier, section } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTests();
  }, [tier, section]);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`, {
        withCredentials: true
      });
      
      // Filter tests by tier and category
      const tierKey = tier === 'basic' ? 'basic' : tier === 'comps-ready' ? 'comps_ready' : 'advanced';
      
      // response.data is directly an array, not wrapped in an object
      const allTests = Array.isArray(response.data) ? response.data : [];
      const filtered = allTests.filter(test => 
        test.tier === tierKey && test.category === section
      );
      
      setTests(filtered);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = (testId) => {
    window.location.href = `/tests/${testId}`;
  };

  const handleDeleteTest = async (testId, testTitle) => {
    if (!window.confirm(`Delete "${testTitle}"? This removes the test, its questions, and all attempt history. This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/tests/${testId}`, { withCredentials: true });
      setTests(prev => prev.filter(t => t.id !== testId));
      toast.success('Test deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete test');
    }
  };

  const handleToggleMix = async (test) => {
    const newValue = !test.randomize_questions;
    try {
      await axios.put(
        `${API}/admin/tests/${test.id}/randomize`,
        { randomize_questions: newValue },
        { withCredentials: true }
      );
      setTests(prev => prev.map(t => t.id === test.id ? { ...t, randomize_questions: newValue } : t));
      toast.success(newValue ? 'Questions will now be mixed for each attempt' : 'Question order restored to fixed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update test');
    }
  };

  const sectionName = sectionNames[section] || section;
  const tierName = tierNames[tier] || tier;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => window.location.href = `/testing-center/${tier}`}
              className="retro-button flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {tierName}
            </button>
          </div>

          <h1 className="text-5xl md:text-6xl retro-title mb-4 text-center">{sectionName}</h1>
          <p className="text-xl text-center text-[#6B4423] mb-12">{tierName} Level Tests</p>

          {/* Tests List */}
          {loading ? (
            <div className="text-center py-12" data-testid="tests-loading">
              <p className="text-xl text-[#6B4423]">Loading tests...</p>
            </div>
          ) : tests.length === 0 ? (
            <div className="cassette-card p-12 text-center" data-testid="tests-empty-state">
              <p className="text-2xl text-[#6B4423] mb-4">No tests available yet</p>
              <p className="text-[#6B4423]">Check back soon! New tests are being added regularly.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="tests-list">
              {tests.map((test) => (
                <div key={test.id} className="cassette-card p-6" data-testid={`test-card-${test.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-2xl retro-title text-[#2C1810]">{test.title}</h3>
                        {test.randomize_questions && (
                          <span
                            className="text-xs font-bold px-2 py-1 rounded-full bg-[#4D9E7C] text-white flex items-center gap-1"
                            data-testid={`test-mixed-badge-${test.id}`}
                          >
                            <Shuffle className="w-3 h-3" /> Mixed
                          </span>
                        )}
                      </div>
                      <p className="text-[#6B4423] mb-3">{test.description}</p>
                      <div className="flex gap-4 text-sm text-[#6B4423]">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {test.question_count || 0} questions
                        </span>
                        {test.time_limit && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {test.time_limit} minutes
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:ml-4">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleToggleMix(test)}
                            className={`retro-button-small flex items-center justify-center gap-2 ${
                              test.randomize_questions ? 'bg-[#4D9E7C] text-white' : ''
                            }`}
                            title="Toggle random question & answer order per attempt"
                            data-testid={`mix-test-button-${test.id}`}
                          >
                            <Shuffle className="w-4 h-4" />
                            {test.randomize_questions ? 'Mixed' : 'Mix'}
                          </button>
                          <button
                            onClick={() => handleDeleteTest(test.id, test.title)}
                            className="retro-button-small flex items-center justify-center gap-2 bg-[#E85D4D] text-white hover:bg-[#d14a3b]"
                            title="Delete this test"
                            data-testid={`delete-test-button-${test.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleStartTest(test.id)}
                        className="retro-button flex items-center justify-center gap-2 w-full md:w-auto"
                        data-testid={`start-test-button-${test.id}`}
                      >
                        <Play className="w-4 h-4" />
                        Start Test
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
